import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  Table,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import React, {
  createContext,
  ReactElement,
  useContext,
  useState,
} from "react";
import { FormattedMessage } from "react-intl";
import { z } from "zod";

import { useLocalStorage } from "~frontend/hooks/useLocalStorage";
import { palette } from "~frontend/theme";

const AUTOADVANCE_ENABLED_KEY = "stwTexterAutoadvanceEnabled";
const AUTOADVANCE_ENABLED_SCHEMA = z.boolean();

export type TexterSettingsContextValue = {
  showTexterSettingsDialog: () => void;
  autoadvanceEnabled: boolean;
};

const TexterSettingsContext = createContext<TexterSettingsContextValue>({
  showTexterSettingsDialog: () => {},
  autoadvanceEnabled: false,
});

export function useTexterSettings(): TexterSettingsContextValue {
  const ctx = useContext(TexterSettingsContext);
  return ctx;
}

function KeyboardShortcutKeys({
  keys,
  note,
  description,
}: {
  keys: React.ReactNode;
  description: React.ReactNode;
  note?: React.ReactNode;
}) {
  return (
    <TableRow>
      <TableCell sx={{ padding: "8px 0" }}>
        <Typography display="inline" variant="body1Bold">
          {keys}
        </Typography>
        {note && (
          <Typography display="inline" variant="body2">
            {" "}
            ({note})
          </Typography>
        )}
      </TableCell>
      <TableCell sx={{ padding: "8px 0" }}>
        <Typography variant="body2">{description}</Typography>
      </TableCell>
    </TableRow>
  );
}

function TexterSettingsDialog({
  autoadvanceEnabled,
  setAutoadvanceEnabled,
  onClose,
}: {
  autoadvanceEnabled: boolean;
  setAutoadvanceEnabled: (enabled: boolean) => void;
  onClose: () => void;
}) {
  const [autoadvanceToggle, setAutoadvanceToggle] =
    useState(autoadvanceEnabled);

  function handleSave() {
    setAutoadvanceEnabled(autoadvanceToggle);
    onClose();
  }

  return (
    <Dialog open onClose={onClose}>
      <DialogTitle sx={{ paddingLeft: "48px" }}>
        <FormattedMessage
          defaultMessage="Texter Settings and Keyboard Shortcuts"
          description="Title for the keyboard shortcut and texter setting dialog (in both texter and thread views)"
        />
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            backgroundColor: palette.lightestGray,
            padding: "16px 24px 24px",
          }}
        >
          <Table>
            <KeyboardShortcutKeys
              keys={
                <FormattedMessage
                  defaultMessage="Shift + Up"
                  description="Keyboard shortcut for holding shift and pressing the up arrow"
                />
              }
              description={
                <FormattedMessage
                  defaultMessage="Move to the previous thread"
                  description="Keyboard shortcut action for moving to the previous thread"
                />
              }
            />
            <KeyboardShortcutKeys
              keys={
                <FormattedMessage
                  defaultMessage="Shift + Down"
                  description="Keyboard shortcut for holding shift and pressing the down arrow"
                />
              }
              description={
                <FormattedMessage
                  defaultMessage="Move to the next thread"
                  description="Keyboard shortcut action for moving to the next thread"
                />
              }
            />
            <KeyboardShortcutKeys
              keys={
                <FormattedMessage
                  defaultMessage="Shift + Space"
                  description="Keyboard shortcut for holding shift and pressing the space bar"
                />
              }
              description={
                <FormattedMessage
                  defaultMessage="Open/close collapsible tabs and/or move to different tab"
                  description="Keyboard shortcut action for switching between tabs"
                />
              }
            />
            <KeyboardShortcutKeys
              keys={
                <FormattedMessage
                  defaultMessage="Tab"
                  description="Keyboard shortcut for pressing tab"
                />
              }
              note={
                <FormattedMessage
                  defaultMessage="from Responses"
                  description="Note that keyboard shortcut only works from the Responses tab"
                />
              }
              description={
                <FormattedMessage
                  defaultMessage="Move through response scripts"
                  description="Keyboard shortcut action for switching between response scripts"
                />
              }
            />
            <KeyboardShortcutKeys
              keys={
                <FormattedMessage
                  defaultMessage="Enter"
                  description="Keyboard shortcut for pressing enter"
                />
              }
              note={
                <FormattedMessage
                  defaultMessage="from Responses"
                  description="Note that keyboard shortcut only works from the Responses tab"
                />
              }
              description={
                <FormattedMessage
                  defaultMessage="Select response script"
                  description="Keyboard shortcut action for selecting a response script"
                />
              }
            />
            <KeyboardShortcutKeys
              keys={
                <FormattedMessage
                  defaultMessage="Command/Control + Enter"
                  description="Keyboard shortcut for holding command/control and pressing enter"
                />
              }
              description={
                <FormattedMessage
                  defaultMessage="Send message"
                  description="Keyboard shortcut action for sending the current message"
                />
              }
            />
          </Table>
        </Box>
        <FormControlLabel
          sx={{ marginTop: "16px", marginLeft: "16px" }}
          label={
            <FormattedMessage
              defaultMessage="Auto-advance to the next thread after sending a message"
              description="Label for the auto-advance setting"
            />
          }
          control={
            <Switch
              checked={autoadvanceToggle}
              onChange={e => setAutoadvanceToggle(e.target.checked)}
            />
          }
        />
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="tertiary" onClick={onClose}>
          <FormattedMessage
            defaultMessage="Cancel"
            description="Button label for canceling the texter settings dialog"
          />
        </Button>
        <Button variant="contained" color="primary" onClick={handleSave}>
          <FormattedMessage
            defaultMessage="Save"
            description="Button label for saving the texter settings"
          />
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function TexterSettingsProvider({
  children,
}: {
  children: React.ReactNode;
}): ReactElement {
  const [autoadvanceEnabled, setAutoadvanceEnabled] = useLocalStorage(
    AUTOADVANCE_ENABLED_KEY,
    AUTOADVANCE_ENABLED_SCHEMA,
    false
  );
  const [dialogOpen, setDialogOpen] = useState(false);

  const showTexterSettingsDialog = () => {
    setDialogOpen(true);
  };

  const value = {
    showTexterSettingsDialog,
    autoadvanceEnabled,
  };

  return (
    <TexterSettingsContext.Provider value={value}>
      {children}
      {dialogOpen && (
        <TexterSettingsDialog
          autoadvanceEnabled={autoadvanceEnabled}
          setAutoadvanceEnabled={setAutoadvanceEnabled}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </TexterSettingsContext.Provider>
  );
}
